import { useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

interface Hospital {
  rank_tier: string;
  name: string;
  city: string;
  state: string;
  sector: string;
  supports: string[];
  notes: string;
  verification_basis: string[];
  verification_links: string[];
  coordinates?: { lat: number; lng: number };
  mapUrl?: string;
}

// Custom marker icons for different tiers
const createCustomIcon = (tier: string) => {
  const color = tier === 'A' ? '#F59E0B' : '#3B82F6';
  const svgIcon = `
    <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26c0-8.837-7.163-16-16-16z" fill="${color}"/>
      <circle cx="16" cy="16" r="6" fill="white"/>
    </svg>
  `;
  
  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42]
  });
};

// Component to fit map bounds to show all markers
function MapBounds({ hospitals }: { hospitals: typeof mockHospitals }) {
  const map = useMap();
  
  useMemo(() => {
    if (hospitals.length > 0) {
      const bounds = L.latLngBounds(
        hospitals
          .filter(h => h.coordinates)
          .map(h => [h.coordinates!.lat, h.coordinates!.lng] as [number, number])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [hospitals, map]);
  
  return null;
}

export default function HospitalDirectory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const [selectedSector, setSelectedSector] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);

  // SEO Configuration
  useSEO({
    title: 'India Hospital Directory | Cervical Cancer Screening & Treatment Centers',
    description: 'Find verified cervical cancer screening and treatment facilities across India. Directory of government hospitals, cancer institutes, and NCG-verified centers offering Pap tests, HPV screening, and comprehensive cancer care.',
    keywords: 'cervical cancer hospitals India, cancer screening centers, Pap test hospitals, HPV testing centers, government cancer hospitals, cancer treatment India',
    canonical: '/hospital-directory',
    schema: generateWebPageSchema(
      'Hospital Directory - Cervical Cancer Care Facilities in India',
      'Comprehensive directory of verified hospitals and cancer care centers across India',
      '/hospital-directory'
    ),
  });

  const hospitals: Hospital[] = [
    {
      rank_tier: 'A',
      name: 'Tata Memorial Hospital',
      city: 'Mumbai',
      state: 'Maharashtra',
      sector: 'Public Trust',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'National apex cancer centre; comprehensive oncology services.',
      verification_basis: ['NCG (National Cancer Grid)'],
      verification_links: ['https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 19.0176, lng: 72.8561 },
      mapUrl: 'https://maps.app.goo.gl/NdmnRado6J5sGXU97'
    },
    {
      rank_tier: 'A',
      name: 'Homi Bhabha Cancer Hospital',
      city: 'Sangrur',
      state: 'Punjab',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Tata Memorial Centre network hospital; oncology services including radiation department listing.',
      verification_basis: ['TMC network / HBCH site'],
      verification_links: ['https://hbchs.tmc.gov.in/DEPTRO'],
      coordinates: { lat: 30.2450, lng: 75.8420 },
      mapUrl: 'https://maps.app.goo.gl/cY9r4cH2FbWB8Fq1A'
    },
    {
      rank_tier: 'A',
      name: 'AIIMS IRCH',
      city: 'New Delhi',
      state: 'Delhi',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Government tertiary cancer centre referenced in RCC list document.',
      verification_basis: ['MoHFW RCC list (PDF)'],
      verification_links: ['https://mohfw.gov.in/sites/default/files/8988949695nccp5_0.pdf'],
      coordinates: { lat: 28.5672, lng: 77.2100 },
      mapUrl: 'https://maps.app.goo.gl/EEDJ7AF44CPepYMMA'
    },
    {
      rank_tier: 'A',
      name: 'Gujarat Cancer Research Institute',
      city: 'Ahmedabad',
      state: 'Gujarat',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Listed in MoHFW RCC list document.',
      verification_basis: ['MoHFW RCC list (PDF)'],
      verification_links: ['https://mohfw.gov.in/sites/default/files/8988949695nccp5_0.pdf'],
      coordinates: { lat: 23.0225, lng: 72.5714 },
      mapUrl: 'https://maps.app.goo.gl/fTtvcrLysjiSjxEE7'
    },
    {
      rank_tier: 'A',
      name: 'RCC Thiruvananthapuram',
      city: 'Thiruvananthapuram',
      state: 'Kerala',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Listed in MoHFW RCC list document.',
      verification_basis: ['MoHFW RCC list (PDF)'],
      verification_links: ['https://mohfw.gov.in/sites/default/files/8988949695nccp5_0.pdf'],
      coordinates: { lat: 8.5241, lng: 76.9366 },
      mapUrl: 'https://maps.app.goo.gl/81TxBGRwsjA81Xet5'
    },
    {
      rank_tier: 'A',
      name: 'PGIMER',
      city: 'Chandigarh',
      state: 'Chandigarh',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Referenced in MoHFW RCC list document.',
      verification_basis: ['MoHFW RCC list (PDF)'],
      verification_links: ['https://mohfw.gov.in/sites/default/files/8988949695nccp5_0.pdf'],
      coordinates: { lat: 30.7646, lng: 76.7687 },
      mapUrl: 'https://maps.app.goo.gl/dZQ4McgXVVHyiKno9'
    },
    {
      rank_tier: 'A',
      name: 'Kidwai Memorial Institute',
      city: 'Bengaluru',
      state: 'Karnataka',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Commonly used state oncology institute; verify in NCG/RCC directories before publishing locally.',
      verification_basis: ['NCG (confirm on member list)'],
      verification_links: ['https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 12.9716, lng: 77.5946 },
      mapUrl: 'https://maps.app.goo.gl/BTRPyytoh3yd4p657'
    },
    {
      rank_tier: 'A',
      name: 'Cancer Institute (WIA)',
      city: 'Chennai',
      state: 'Tamil Nadu',
      sector: 'Trust',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Major cancer institute; verify in NCG directories for your final page.',
      verification_basis: ['NCG (confirm on member list)'],
      verification_links: ['https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 13.0827, lng: 80.2707 },
      mapUrl: 'https://maps.app.goo.gl/bzSUhLSdGZMJaDz97'
    },
    {
      rank_tier: 'A',
      name: 'MNJ Institute of Oncology',
      city: 'Hyderabad',
      state: 'Telangana',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Regional cancer centre in Telangana; verify in RCC/NCG list for publishing.',
      verification_basis: ['NCG / RCC (verify)'],
      verification_links: ['https://www.ncgindia.org/hospitals-and-institutions/members', 'https://mohfw.gov.in/sites/default/files/8988949695nccp5_0.pdf'],
      coordinates: { lat: 17.3850, lng: 78.4867 },
      mapUrl: 'https://maps.app.goo.gl/URL9tLSAa9vx5x217'
    },
    {
      rank_tier: 'A',
      name: 'B Borooah Cancer Institute',
      city: 'Guwahati',
      state: 'Assam',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Major regional cancer institute; verify in NCG member list for final publishing.',
      verification_basis: ['NCG (confirm on member list)'],
      verification_links: ['https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 26.1445, lng: 91.7362 },
      mapUrl: 'https://maps.app.goo.gl/mcxXS2UyRCaXs3r1A'
    },
    {
      rank_tier: 'A',
      name: 'Chittaranjan National Cancer Institute',
      city: 'Kolkata',
      state: 'West Bengal',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Major government cancer institute; verify on NCG member list for publishing.',
      verification_basis: ['NCG (confirm on member list)'],
      verification_links: ['https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 22.5726, lng: 88.3639 },
      mapUrl: 'https://maps.app.goo.gl/sWxK8yfnUKymZM4r5'
    },
    {
      rank_tier: 'A',
      name: 'Tata Medical Center',
      city: 'Kolkata',
      state: 'West Bengal',
      sector: 'Trust',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Tata trust cancer centre; verify in NCG member list for publishing.',
      verification_basis: ['NCG (confirm on member list)'],
      verification_links: ['https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 22.5726, lng: 88.3639 },
      mapUrl: 'https://maps.app.goo.gl/kUtR6ZBx8cESGwBb6'
    },
    {
      rank_tier: 'B',
      name: 'Delhi State Cancer Institute',
      city: 'New Delhi',
      state: 'Delhi',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Appears in an NCG list PDF snapshot; verify in current NCG member list for publishing.',
      verification_basis: ['NCG list (PDF snapshot) / NCG member list'],
      verification_links: ['https://eqas.ncgindia.org/ncglist/ncglistversion1Jan2017.PDF', 'https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 28.6139, lng: 77.2090 },
      mapUrl: 'https://maps.app.goo.gl/39kVqiFYBbWXxrN96'
    },
    {
      rank_tier: 'B',
      name: 'Dharamshila Narayana Hospital',
      city: 'New Delhi',
      state: 'Delhi',
      sector: 'Private',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Listed in NCG list PDF snapshot; verify in current NCG/NABH directories.',
      verification_basis: ['NCG list (PDF snapshot) / NABH (optional check)'],
      verification_links: ['https://eqas.ncgindia.org/ncglist/ncglistversion1Jan2017.PDF', 'https://nabh.co/find-a-healthcare-organisation/'],
      coordinates: { lat: 28.6139, lng: 77.2090 },
      mapUrl: 'https://maps.app.goo.gl/2S8LuoKAcVPP5TBc6'
    },
    {
      rank_tier: 'B',
      name: 'Pt JNM Medical College RCC',
      city: 'Raipur',
      state: 'Chhattisgarh',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Listed in MoHFW RCC list document.',
      verification_basis: ['MoHFW RCC list (PDF)'],
      verification_links: ['https://mohfw.gov.in/sites/default/files/8988949695nccp5_0.pdf'],
      coordinates: { lat: 21.2514, lng: 81.6296 },
      mapUrl: 'https://maps.app.goo.gl/xsv6Vyap5UfuW4Hg9'
    },
    {
      rank_tier: 'B',
      name: 'CMC Vellore',
      city: 'Vellore',
      state: 'Tamil Nadu',
      sector: 'Trust',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Major tertiary centre; verify in NABH/NCG directories for publishing.',
      verification_basis: ['NABH / NCG (verify)'],
      verification_links: ['https://nabh.co/find-a-healthcare-organisation/', 'https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 12.9165, lng: 79.1325 },
      mapUrl: 'https://maps.app.goo.gl/7iF9cNcbLomb4Qzy8'
    },
    {
      rank_tier: 'B',
      name: 'Apollo Hospitals',
      city: 'Chennai',
      state: 'Tamil Nadu',
      sector: 'Private',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Private oncology; verify NABH-accreditation + oncology services for the specific unit/branch you publish.',
      verification_basis: ['NABH (verify specific branch)'],
      verification_links: ['https://nabh.co/find-a-healthcare-organisation/'],
      coordinates: { lat: 13.0827, lng: 80.2707 },
      mapUrl: 'https://maps.app.goo.gl/9R1G8dqEnviJbnuD6'
    },
    {
      rank_tier: 'B',
      name: 'HCG Cancer Centre',
      city: 'Bengaluru',
      state: 'Karnataka',
      sector: 'Private',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Private cancer centre network; verify the specific centre via NABH/NCG before publishing.',
      verification_basis: ['NABH / NCG (verify specific unit)'],
      verification_links: ['https://nabh.co/find-a-healthcare-organisation/', 'https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 12.9716, lng: 77.5946 },
      mapUrl: 'https://maps.app.goo.gl/sjYh2YrM6Yopoi1n9'
    },
    {
      rank_tier: 'B',
      name: 'Rajiv Gandhi Cancer Institute',
      city: 'New Delhi',
      state: 'Delhi',
      sector: 'Private',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Major cancer institute; verify in NCG/NABH directories for publishing.',
      verification_basis: ['NCG / NABH (verify)'],
      verification_links: ['https://www.ncgindia.org/hospitals-and-institutions/members', 'https://nabh.co/find-a-healthcare-organisation/'],
      coordinates: { lat: 28.6139, lng: 77.2090 },
      mapUrl: 'https://maps.app.goo.gl/qFHfnF89ftxS1Q1cA'
    },
    {
      rank_tier: 'B',
      name: 'Sassoon General Hospital',
      city: 'Pune',
      state: 'Maharashtra',
      sector: 'Government',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Use NCG member list filter for Maharashtra to publish confirmed Pune-region cancer centres (recommended).',
      verification_basis: ['NCG (use state filter)'],
      verification_links: ['https://www.ncgindia.org/hospitals-and-institutions/members'],
      coordinates: { lat: 18.5204, lng: 73.8567 },
      mapUrl: 'https://maps.app.goo.gl/411Eogc7WaqJ7zEDA'
    },
    {
      rank_tier: 'B',
      name: 'Kokilaben Ambani Hospital',
      city: 'Mumbai',
      state: 'Maharashtra',
      sector: 'Private',
      supports: ['screening', 'diagnosis', 'treatment'],
      notes: 'Private multi-specialty; verify oncology + NABH for the exact unit you list.',
      verification_basis: ['NABH (verify)'],
      verification_links: ['https://nabh.co/find-a-healthcare-organisation/'],
      coordinates: { lat: 19.0760, lng: 72.8777 },
      mapUrl: 'https://maps.app.goo.gl/A21cBZronkVNzULw5'
    }
  ];

  const states = ['all', ...Array.from(new Set(hospitals.map(h => h.state)))].sort();
  const tiers = ['all', 'A', 'B'];
  const sectors = ['all', 'Government', 'Government / State', 'Government / TMC network', 'Private', 'Private / Trust', 'Public Trust', 'Public Trust / National Apex'];
  const services = ['all', 'screening', 'diagnosis', 'treatment'];

  const filteredHospitals = hospitals.filter(hospital => {
    const matchesSearch = hospital.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         hospital.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         hospital.state.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesState = selectedState === 'all' || hospital.state === selectedState;
    const matchesTier = selectedTier === 'all' || hospital.rank_tier === selectedTier;
    const matchesSector = selectedSector === 'all' || hospital.sector === selectedSector;
    const matchesService = selectedService === 'all' || hospital.supports.includes(selectedService);
    return matchesSearch && matchesState && matchesTier && matchesSector && matchesService;
  });

  const governmentCount = hospitals.filter(h => h.sector.includes('Government')).length;
  const tierACount = hospitals.filter(h => h.rank_tier === 'A').length;

  const scrollToHospital = (hospitalName: string) => {
    const element = document.getElementById(`hospital-${hospitalName}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('ring-4', 'ring-teal-500');
      setTimeout(() => {
        element.classList.remove('ring-4', 'ring-teal-500');
      }, 2000);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'A': return 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white';
      case 'B': return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getSectorColor = (sector: string) => {
    if (sector.includes('Government')) return 'bg-green-100 text-green-700 border-green-300';
    if (sector.includes('Private')) return 'bg-blue-100 text-blue-700 border-blue-300';
    if (sector.includes('Trust')) return 'bg-purple-100 text-purple-700 border-purple-300';
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-teal-50/20 to-white">
      <Navbar />

      <div className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold font-display text-gray-800 mb-4">
              Hospital <span className="text-primary">Directory</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find verified cervical cancer screening and treatment facilities across India
            </p>
            <div className="flex items-center justify-center gap-6 mt-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-sm text-gray-600 font-medium">Tier A: {tierACount} facilities</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-gray-600 font-medium">Government: {governmentCount} facilities</span>
              </div>
            </div>
          </div>

          {/* Map Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-teal-600 to-blue-600 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <i className="ri-map-pin-line text-3xl text-white"></i>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Interactive Hospital Map</h2>
                      <p className="text-white/90 text-sm">Explore {filteredHospitals.length} verified cancer care facilities across India</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                      <span className="text-white text-sm font-medium">Tier A: {filteredHospitals.filter(h => h.rank_tier === 'A').length}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                      <span className="text-white text-sm font-medium">Tier B: {filteredHospitals.filter(h => h.rank_tier === 'B').length}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="relative h-[600px] bg-gray-100">
                {filteredHospitals.length > 0 && filteredHospitals.some(h => h.coordinates) ? (
                  <MapContainer
                    center={[20.5937, 78.9629]}
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapBounds hospitals={filteredHospitals.filter(h => h.coordinates)} />
                    {filteredHospitals
                      .filter(h => h.coordinates)
                      .map((hospital) => (
                        <Marker
                          key={hospital.name}
                          position={[hospital.coordinates!.lat, hospital.coordinates!.lng]}
                          icon={createCustomIcon(hospital.rank_tier)}
                          eventHandlers={{
                            click: () => {
                              scrollToHospital(hospital.name);
                            }
                          }}
                        >
                          <Popup>
                            <div className="p-2 min-w-[200px]">
                              <h3 className="font-bold text-gray-900 mb-1">{hospital.name}</h3>
                              <p className="text-sm text-gray-600 mb-1">{hospital.city}, {hospital.state}</p>
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  hospital.rank_tier === 'A' 
                                    ? 'bg-amber-100 text-amber-800' 
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  Tier {hospital.rank_tier}
                                </span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  hospital.sector.includes('Government')
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-purple-100 text-purple-800'
                                }`}>
                                  {hospital.sector.includes('Government') ? 'Government' : hospital.sector.includes('Private') ? 'Private' : 'Trust'}
                                </span>
                              </div>
                              <button
                                onClick={() => scrollToHospital(hospital.name)}
                                className="w-full bg-teal-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                View Details
                              </button>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                  </MapContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">No hospitals to display on map</p>
                  </div>
                )}
              </div>

              {/* Quick Access Panel */}
              {filteredHospitals.length > 0 && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 border-t border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Access</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {filteredHospitals.slice(0, 8).map((hospital) => (
                      <button
                        key={hospital.name}
                        onClick={() => scrollToHospital(hospital.name)}
                        className="flex items-center gap-2 bg-white px-4 py-3 rounded-lg shadow-sm hover:shadow-md transition-all hover:scale-105 text-left group cursor-pointer"
                      >
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          hospital.rank_tier === 'A' ? 'bg-amber-400' : 'bg-blue-400'
                        }`}></div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate group-hover:text-teal-600 transition-colors">
                            {hospital.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{hospital.city}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  {filteredHospitals.length > 8 && (
                    <p className="text-center mt-4 text-sm text-gray-600">
                      <a href="#hospital-list" className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">
                        View all {filteredHospitals.length} hospitals below ↓
                      </a>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Hospital Cards Grid */}
          <div id="hospital-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHospitals.map((hospital) => (
              <div
                key={hospital.name}
                id={`hospital-${hospital.name}`}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                onClick={() => setSelectedHospital(hospital)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${getTierColor(hospital.rank_tier)}`}>
                      TIER {hospital.rank_tier}
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getSectorColor(hospital.sector)}`}>
                      {hospital.sector.includes('Government') ? 'Government' : hospital.sector.includes('Private') ? 'Private' : 'Trust'}
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2 min-h-[3.5rem]">{hospital.name}</h3>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <i className="ri-map-pin-line text-primary"></i>
                      <span className="text-sm">{hospital.city}, {hospital.state}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Services Available</div>
                    <div className="flex flex-wrap gap-2">
                      {hospital.supports.map((service, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium capitalize"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Verification</div>
                    <div className="flex flex-wrap gap-2">
                      {hospital.verification_basis.map((basis, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md border border-green-200 font-medium"
                        >
                          <i className="ri-shield-check-line mr-1"></i>
                          {basis.split('(')[0].trim()}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedHospital(hospital);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary to-teal-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-information-line"></i>
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredHospitals.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-bold text-gray-800 mb-2">No hospitals found</h3>
              <p className="text-gray-600">Try adjusting your search filters</p>
            </div>
          )}

          {/* Important Information */}
          <div className="mt-16 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-8">
            <div className="flex items-start gap-4">
              <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
              <div>
                <h3 className="font-bold text-gray-800 mb-3">Important Information</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-yellow-600 mt-0.5 flex-shrink-0"></i>
                    <span>This directory is for informational purposes only. We do not have partnerships with these facilities.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-yellow-600 mt-0.5 flex-shrink-0"></i>
                    <span>Please contact hospitals directly to verify services, availability, and costs.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-yellow-600 mt-0.5 flex-shrink-0"></i>
                    <span>Government hospitals often provide free or subsidized screening and treatment.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-yellow-600 mt-0.5 flex-shrink-0"></i>
                    <span>All facilities listed are verified through NCG, MoHFW, or NABH directories.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hospital Detail Modal */}
      {selectedHospital && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedHospital(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-primary to-teal-600 p-6 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <i className="ri-hospital-line text-3xl text-white"></i>
                <h2 className="text-2xl font-bold text-white">Hospital Details</h2>
              </div>
              <button
                onClick={() => setSelectedHospital(null)}
                className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-all duration-300 cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-white"></i>
              </button>
            </div>

            <div className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className={`px-4 py-2 rounded-full text-sm font-bold ${getTierColor(selectedHospital.rank_tier)}`}>
                  TIER {selectedHospital.rank_tier}
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-semibold border ${getSectorColor(selectedHospital.sector)}`}>
                  {selectedHospital.sector}
                </div>
              </div>

              <h3 className="text-3xl font-bold text-gray-800 mb-4">{selectedHospital.name}</h3>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-full">
                    <i className="ri-map-pin-line text-xl text-primary"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase font-semibold">Location</div>
                    <div className="text-gray-800 font-medium">{selectedHospital.city}, {selectedHospital.state}</div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-500 uppercase mb-3">Services Available</h4>
                <div className="flex flex-wrap gap-3">
                  {selectedHospital.supports.map((service, idx) => (
                    <span
                      key={idx}
                      className="px-4 py-2 bg-primary/10 text-primary text-sm rounded-full font-semibold capitalize"
                    >
                      <i className="ri-check-line mr-1"></i>
                      {service}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-500 uppercase mb-3">About This Facility</h4>
                <p className="text-gray-700 leading-relaxed">{selectedHospital.notes}</p>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-bold text-gray-500 uppercase mb-3">Verification Sources</h4>
                <div className="space-y-3">
                  {selectedHospital.verification_basis.map((basis, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                      <i className="ri-shield-check-line text-2xl text-green-600 flex-shrink-0"></i>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800 mb-1">{basis}</div>
                        {selectedHospital.verification_links[idx] && (
                          <a
                            href={selectedHospital.verification_links[idx]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1 cursor-pointer"
                          >
                            View Verification Source
                            <i className="ri-external-link-line"></i>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedHospital.coordinates && (
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-gray-500 uppercase mb-3">Location Map</h4>
                  <div className="rounded-xl overflow-hidden h-64 bg-gray-100">
                    <iframe
                      src={`https://maps.google.com/maps?q=${selectedHospital.coordinates.lat},${selectedHospital.coordinates.lng}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <a
                  href={selectedHospital.mapUrl || `https://www.google.com/maps/search/?api=1&query=${selectedHospital.coordinates?.lat},${selectedHospital.coordinates?.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-teal-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-map-pin-line"></i>
                  Get Directions
                </a>
                <button
                  onClick={() => setSelectedHospital(null)}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
